import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import type { ShippingOption, ProductCustomizationOption } from '@tempefab-model/tempe-fabrication-client';
import { tempeFabricationClientWrapper } from '../services/tempeFabricationClientWrapper';

// Cart item interface
export interface CartItem {
  id: string; // Original product ID
  cartItemId: string; // Unique identifier (productId + customization)
  name: string;
  sku: string;
  price: number;
  quantity: number;
  image: string;
  discount?: number;
  shippingOptions?: ShippingOption[];
  customization?: ProductCustomizationOption;
}

/**
 * Generate a unique cart item ID based on product ID and customization
 * @param productId - The product ID
 * @param customization - The customization option (if any)
 * @returns Unique cart item identifier
 */
const generateCartItemId = (productId: string, customization?: ProductCustomizationOption): string => {
  if (!customization) {
    return productId;
  }
  // Create unique key: productId + customization name
  return `${productId}__${customization.name}`;
};

// Cart context interface
interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'quantity' | 'cartItemId'>, quantity?: number) => void;
  removeItem: (cartItemId: string) => void;
  updateQuantity: (cartItemId: string, quantity: number) => void;
  incrementQuantity: (cartItemId: string) => void;
  decrementQuantity: (cartItemId: string) => void;
  clearCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
  getItemQuantity: (productId: string, customization?: ProductCustomizationOption) => number;
  getCartHash: () => string;
}

// Create context
const CartContext = createContext<CartContextType | undefined>(undefined);

// Local storage key
const CART_STORAGE_KEY = 'tempefab_shopping_cart';
const CHECKOUT_SESSION_KEY = 'tempefab_checkout_session';

// Provider component
export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);

  /**
   * Cancel payment intent and clear session when cart changes
   * This ensures payment intent is invalidated when cart is modified
   */
  const handleCartChange = useCallback(async () => {
    // Read session from localStorage
    const storedSession = localStorage.getItem(CHECKOUT_SESSION_KEY);
    if (!storedSession) return;

    try {
      const session = JSON.parse(storedSession);

      // Check if there's a valid payment intent
      if (session.paymentIntentId && session.ttl) {
        const now = Date.now();
        const ttlMs = session.ttl * 1000; // Convert to milliseconds
        const isExpired = now > ttlMs;

        if (!isExpired) {
          // Cancel the payment intent (cart changed while checkout was active)
          try {
            await tempeFabricationClientWrapper.cancelPaymentIntent(session.paymentIntentId);
          } catch (error) {
            // Non-critical error - payment intent cancellation failed
          }

          // Clear the entire session
          localStorage.removeItem(CHECKOUT_SESSION_KEY);
        }
      }
    } catch (error) {
      console.error('[CartContext] Error handling cart change:', error);
    }
  }, []);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart) as CartItem[];

        // Migrate old cart items to include cartItemId if missing
        const migratedCart = parsedCart.map(item => {
          if (!item.cartItemId) {
            // Generate cartItemId for old items
            return {
              ...item,
              cartItemId: generateCartItemId(item.id, item.customization)
            };
          }
          return item;
        });

        console.log('[CartContext] Loaded cart from localStorage, migrated items:', migratedCart.length);
        setItems(migratedCart);
      } catch (error) {
        console.error('[CartContext] Failed to parse cart from localStorage:', error);
        // Clear corrupted cart data
        localStorage.removeItem(CART_STORAGE_KEY);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    console.log('[CartContext] Saving cart to localStorage, items count:', items.length);
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // Add item to cart or update quantity if it exists
  const addItem = (item: Omit<CartItem, 'quantity' | 'cartItemId'>, quantity: number = 1) => {
    setItems((prevItems) => {
      // Generate unique cart item ID
      const cartItemId = generateCartItemId(item.id, item.customization);

      // Find existing item by cartItemId (product + customization combo)
      const existingItem = prevItems.find((i) => i.cartItemId === cartItemId);

      if (existingItem) {
        // Exact same product+customization exists, just increment quantity
        return prevItems.map((i) =>
          i.cartItemId === cartItemId ? {
            ...i,
            quantity: i.quantity + quantity,
          } : i
        );
      } else {
        // New product+customization combination, add as separate line item
        return [...prevItems, { ...item, cartItemId, quantity }];
      }
    });

    // Track product added to cart for attribution (fire-and-forget)
    tempeFabricationClientWrapper.trackProductAddedToCart(item.id, quantity);

    // Cancel payment intent if exists
    handleCartChange();
  };

  // Remove item completely from cart
  const removeItem = (cartItemId: string) => {
    setItems((prevItems) => prevItems.filter((item) => item.cartItemId !== cartItemId));

    // Cancel payment intent if exists
    handleCartChange();
  };

  // Update item quantity (set to specific value)
  const updateQuantity = (cartItemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(cartItemId);
    } else {
      setItems((prevItems) =>
        prevItems.map((item) =>
          item.cartItemId === cartItemId ? { ...item, quantity } : item
        )
      );

      // Cancel payment intent if exists
      handleCartChange();
    }
  };

  // Increment item quantity by 1
  const incrementQuantity = (cartItemId: string) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.cartItemId === cartItemId ? { ...item, quantity: item.quantity + 1 } : item
      )
    );

    // Cancel payment intent if exists
    handleCartChange();
  };

  // Decrement item quantity by 1 (remove if quantity becomes 0)
  const decrementQuantity = (cartItemId: string) => {
    setItems((prevItems) => {
      const item = prevItems.find((i) => i.cartItemId === cartItemId);

      if (!item) return prevItems;

      if (item.quantity <= 1) {
        // Remove item if it's the last one
        return prevItems.filter((i) => i.cartItemId !== cartItemId);
      } else {
        // Decrease quantity
        return prevItems.map((i) =>
          i.cartItemId === cartItemId ? { ...i, quantity: i.quantity - 1 } : i
        );
      }
    });

    // Cancel payment intent if exists
    handleCartChange();
  };

  // Clear entire cart
  const clearCart = () => {
    console.log('[CartContext] Clearing cart, current items:', items.length);
    setItems([]);
    console.log('[CartContext] Cart cleared');
  };

  // Get total number of items in cart
  const getTotalItems = () => {
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  // Get total price of all items in cart (including customization costs)
  const getTotalPrice = () => {
    return items.reduce((total, item) => {
      const itemPrice = item.discount
        ? item.price - (item.price * item.discount / 100)
        : item.price;
      const customizationCost = item.customization?.additionalCost || 0;
      return total + ((itemPrice + customizationCost) * item.quantity);
    }, 0);
  };

  // Get quantity of specific item (by product ID and customization)
  const getItemQuantity = (productId: string, customization?: ProductCustomizationOption) => {
    const cartItemId = generateCartItemId(productId, customization);
    const item = items.find((i) => i.cartItemId === cartItemId);
    return item ? item.quantity : 0;
  };

  // Get hash of cart contents to detect changes
  const getCartHash = () => {
    // Create a deterministic string from cart items (including customization)
    const cartString = items
      .map((item) => `${item.cartItemId}:${item.quantity}:${item.price}:${item.customization?.additionalCost || 0}`)
      .sort() // Sort to ensure consistent ordering
      .join('|');

    // Simple hash function (good enough for detecting cart changes)
    let hash = 0;
    for (let i = 0; i < cartString.length; i++) {
      const char = cartString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString();
  };

  const value: CartContextType = {
    items,
    addItem,
    removeItem,
    updateQuantity,
    incrementQuantity,
    decrementQuantity,
    clearCart,
    getTotalItems,
    getTotalPrice,
    getItemQuantity,
    getCartHash,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

// Custom hook to use cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
